import React from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Box,
  Typography,
  IconButton,
  CircularProgress,
  Alert,
} from "@mui/material";
import {
  Close as CloseIcon,
  Warning as WarningIcon,
} from "@mui/icons-material";
import { useDeleteCaseMutation } from "../../../store/api/apiSlice";
import type { DynamicCase } from "../../../types";

interface DeleteConfirmationModalProps {
  open: boolean;
  onClose: () => void;
  case: DynamicCase;
  onCaseDeleted: (deletedCase: DynamicCase) => void;
  onError: (error: string) => void;
}

export const DeleteConfirmationModal: React.FC<
  DeleteConfirmationModalProps
> = ({ open, onClose, case: caseItem, onCaseDeleted, onError }) => {
  const [deleteCase, { isLoading }] = useDeleteCaseMutation();

  const handleDelete = async () => {
    try {
      await deleteCase(caseItem.id).unwrap();
      onCaseDeleted(caseItem);
      onClose();
    } catch (error: any) {
      console.error("Failed to delete case:", error);
      onError(error.message || "Failed to delete case");
    }
  };

  const handleClose = () => {
    if (!isLoading) {
      onClose();
    }
  };

  if (!caseItem) return null;

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      maxWidth="sm"
      fullWidth
      PaperProps={{
        sx: {
          borderRadius: 2,
        },
      }}
    >
      <DialogTitle
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          pb: 1,
        }}
      >
        <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
          <WarningIcon sx={{ color: "error.main" }} />
          <Typography variant="h6" component="h2" sx={{ fontWeight: 600 }}>
            Delete Case
          </Typography>
        </Box>
        <IconButton
          onClick={handleClose}
          disabled={isLoading}
          sx={{ color: "text.secondary" }}
        >
          <CloseIcon />
        </IconButton>
      </DialogTitle>

      <DialogContent sx={{ pt: 1 }}>
        <Alert severity="error" sx={{ mb: 3 }}>
          <Typography variant="body2">
            <strong>This action cannot be undone.</strong> The case and all
            associated data will be permanently deleted.
          </Typography>
        </Alert>

        <Typography variant="body1" sx={{ mb: 2 }}>
          Are you sure you want to delete this case?
        </Typography>

        {/* Case Summary */}
        <Box
          sx={{
            p: 2,
            backgroundColor: "background.default",
            borderRadius: 1,
            border: 1,
            borderColor: "divider",
          }}
        >
          <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 1 }}>
            {caseItem.title || "Untitled Case"}
          </Typography>

          <Box sx={{ display: "flex", gap: 2, mb: 1 }}>
            <Typography variant="caption" sx={{ color: "text.secondary" }}>
              <strong>Age:</strong>{" "}
              {caseItem.age ? `${caseItem.age} years` : "Not specified"}
            </Typography>
            <Typography variant="caption" sx={{ color: "text.secondary" }}>
              <strong>Gender:</strong> {caseItem.gender || "Not specified"}
            </Typography>
          </Box>

          <Typography
            variant="caption"
            sx={{ color: "text.secondary", display: "block" }}
          >
            <strong>Condition:</strong>{" "}
            {caseItem.primary_condition || "Not specified"}
          </Typography>

          <Typography
            variant="caption"
            sx={{ color: "text.secondary", display: "block" }}
          >
            <strong>Status:</strong> {caseItem.processing_status}
          </Typography>

          <Typography
            variant="caption"
            sx={{ color: "text.secondary", display: "block" }}
          >
            <strong>Created:</strong>{" "}
            {new Date(caseItem.created_at).toLocaleDateString()}
          </Typography>
        </Box>
      </DialogContent>

      <DialogActions sx={{ p: 3, pt: 2 }}>
        <Button
          onClick={handleClose}
          disabled={isLoading}
          sx={{
            color: "text.secondary",
            textTransform: "none",
          }}
        >
          Cancel
        </Button>
        <Button
          onClick={handleDelete}
          variant="contained"
          disabled={isLoading}
          sx={{
            backgroundColor: "error.main",
            color: "text.primary",
            fontWeight: 600,
            textTransform: "none",
            minWidth: 120,
            "&:hover": {
              backgroundColor: "error.dark",
            },
          }}
        >
          {isLoading ? (
            <CircularProgress size={20} color="inherit" />
          ) : (
            "Delete Case"
          )}
        </Button>
      </DialogActions>
    </Dialog>
  );
};
